import React, { Fragment, useEffect, useState } from 'react'
import css from './ProtectionDays.module.css'
import { ReactComponent as PlusIcon } from '../../../../../../../assets/icons/icon-plus.svg'
import { ReactComponent as TrashIcon } from '../../../../../../../assets/icons/icon-trash.svg'
import Button from 'shared/Buttons/Button/Button'
import Cell from './cell/Cell'
import EditButton from 'shared/Buttons/EditButton'
import ChangeRoomSettingsModal from './modal/changeRoomSettings/ChangeRoomSettings'
import SelectTeamModal from 'shared/Modal/SelectTeamModal'
import DefenceModal from '../../../components/modal/DefenceModal'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import { useParams } from 'react-router-dom'
import protectionDayAtom from '../../protectionDay.atom'
import { useSetRecoilState, useRecoilValue } from 'recoil'
import protectionsApi from 'screens/Protection/protection.api'
import { formatDate, formatTime, getTeamStartTime } from '../../../../../utils/formatDate'
import {
  IProtectionDayResponse,
  IRoom,
  IRoomInsideDefence,
  IRoomTeam,
  ITeam,
} from 'screens/Protection/protection.types'
import { activeRoomAtom } from 'screens/Protection/protection.atom'
import eventBus from 'services/eventBus'
import { useNavigate } from 'react-router-dom'
import TrashButton from 'shared/Buttons/TrashButton'
import { truncateText } from 'screens/Protection/utils/textFormate'

export default function ProtectionDays() {
  const ROW_COUNT = 12
  const [rooms, setRooms] = useState<IRoomInsideDefence[]>([])

  const [slots, setSlots] = useState<(IRoomTeam | null)[][]>([])

  const [selectedSlot, setSelectedSlot] = useState<{
    rowIndex: number
    roomIndex: number
  } | null>(null)

  const [isChangeRoomSettingModal, setIsChangeRoomSettingModal] = useState(false)
  const [isChangeDaySettingModal, setIsChangeDaySettingModal] = useState(false)
  const [isSelectTeamModal, setIsSelectTeamModal] = useState(false)
  const [roomModalMode, setRoomModalMode] = useState<'create' | 'update'>('create')

  const { wait } = useHttpLoader()
  const params = useParams()
  const navigate = useNavigate()
  const setCurrentDefence = useSetRecoilState(protectionDayAtom)
  const protectionDay = useRecoilValue(protectionDayAtom)
  const setActiveRoom = useSetRecoilState(activeRoomAtom)

  const createEmptySlots = (roomsLength: number) =>
    Array.from({ length: ROW_COUNT }, () => Array.from({ length: roomsLength }, () => null))

  useEffect(() => {
    if (!params.id) return

    wait(protectionsApi.getDefenceById(params.id), (resp) => {
      if (resp.status !== 'success') return

      setCurrentDefence(resp.body)

      const rooms = resp.body?.rooms ?? []
      setRooms(rooms)

      const initialSlots = createEmptySlots(rooms.length)

      rooms.forEach((room, roomIndex) => {
        room.teams?.forEach((team) => {
          const rowIndex = team.presentationOrder - 1

          if (rowIndex >= 0 && rowIndex < ROW_COUNT) {
            initialSlots[rowIndex][roomIndex] = team
          }
        })
      })

      setSlots(initialSlots)
    })
  }, [params.id])

  const handleRoomCreated = (newRoom: IRoomInsideDefence) => {
    setRooms((prev) => {
      const index = prev.findIndex((room) => room.id === newRoom.id)

      if (index === -1) {
        setSlots((prevSlots) => prevSlots.map((row) => [...row, null]))
        return [...prev, newRoom]
      }

      const updated = [...prev]
      updated[index] = newRoom

      return updated
    })
  }

  const moveTeam = (fromRow: number, fromRoom: number, toRow: number, toRoom: number) => {
    const sourceTeam = slots[fromRow][fromRoom]
    const targetTeam = slots[toRow][toRoom]

    if (!sourceTeam) return

    const targetRoomId = rooms[toRoom]?.id
    const sourceRoomId = rooms[fromRoom]?.id

    if (!targetRoomId || !sourceRoomId) return

    if (!targetTeam) {
      wait(
        protectionsApi.moveTeamToRoom(targetRoomId, sourceTeam.id, {
          presentationOrder: toRow + 1,
        }),
        (resp) => {
          if (resp.status !== 'success') return

          setSlots((prev) => {
            const copy = prev.map((row) => [...row])

            copy[toRow][toRoom] = { ...sourceTeam, presentationOrder: toRow + 1 }
            copy[fromRow][fromRoom] = null

            return copy
          })

          eventBus.pub('ntf.message', {
            type: 'info',
            message: 'Команда успешно перемещена',
          })
        }
      )

      return
    }
    wait(
      Promise.all([
        protectionsApi.moveTeamToRoom(targetRoomId, sourceTeam.id, {
          presentationOrder: toRow + 1,
        }),
        protectionsApi.moveTeamToRoom(sourceRoomId, targetTeam.id, {
          presentationOrder: fromRow + 1,
        }),
      ]),
      () => {
        setSlots((prev) => {
          const copy = prev.map((row) => [...row])

          if (sourceTeam) copy[toRow][toRoom] = { ...sourceTeam, presentationOrder: toRow + 1 }
          if (targetTeam) copy[fromRow][fromRoom] = { ...targetTeam, presentationOrder: fromRow + 1 }

          return copy
        })

        eventBus.pub('ntf.message', {
          type: 'info',
          message: 'Команды успешно поменяны местами',
        })
      }
    )
  }

  const selectedTeam = async (team: ITeam) => {
    if (!selectedSlot) return
    const roomId = rooms[selectedSlot.roomIndex]?.id
    if (!roomId) return

    const newRoomTeam: IRoomTeam = {
      id: team.id,
      name: team.name,
      ownerId: team.ownerId,
      presentationOrder: selectedSlot.rowIndex + 1,
      project: null as any,
    }

    wait(
      protectionsApi.addTeamToRoom(roomId, {
        teamId: newRoomTeam.id,
        presentationOrder: newRoomTeam.presentationOrder,
      }),
      (resp) => {
        if (resp.status !== 'success') return

        eventBus.pub('ntf.message', {
          type: 'info',
          message: 'Команда успешно добавлена',
        })

        setSlots((prev) => {
          const copy = prev.map((row) => [...row])
          copy[selectedSlot.rowIndex][selectedSlot.roomIndex] = newRoomTeam
          return copy
        })
      }
    )

    setSelectedSlot(null)
    setIsSelectTeamModal(false)
  }

  const handleDeleteRoom = async (roomId: IRoomInsideDefence['id']) => {
    if (!roomId) return

    const roomIndex = rooms.findIndex((room) => room.id === roomId)

    wait(protectionsApi.deleteRoom(roomId), (resp) => {
      if (resp.status !== 'success') return

      eventBus.pub('ntf.message', {
        type: 'info',
        message: 'Комната успешно удалена',
      })

      setRooms((prev) => prev.filter((room) => room.id !== roomId))

      setSlots((prev) => prev.map((row) => row.filter((_, index) => index !== roomIndex)))
    })
  }

  const handleDeleteTeam = async (roomId: IRoom['id'], teamId: ITeam['id'], rowIndex: number, roomIndex: number) => {
    wait(protectionsApi.deleteTeamFromRoom(roomId, teamId), (resp) => {
      if (resp.status !== 'success') return

      eventBus.pub('ntf.message', {
        type: 'info',
        message: 'Команда успешно удалена',
      })

      setSlots((prev) => {
        const copy = prev.map((row) => [...row])

        copy[rowIndex][roomIndex] = null

        return copy
      })
    })
  }

  const handleDeleteDefence = async (defenceId: IProtectionDayResponse['id']) => {
    wait(protectionsApi.deleteDefence(defenceId), (resp) => {
      if (resp.status != 'success') return

      eventBus.pub('ntf.message', {
        type: 'info',
        message: 'Защита успешно удалена',
      })

      navigate(-1)
    })
  }

  return (
    <div className={css.wrapper}>
      <div className={css.table}>
        <div className={css.topbar}>
          <div className={css.left}>
            <span className={css.date}>{formatDate(protectionDay?.defence?.startsAt)}</span>

            <div className={css.group}>
              {protectionDay?.defence?.name}
              <EditButton onClick={() => setIsChangeDaySettingModal(!isChangeDaySettingModal)} />
              <TrashButton onClick={() => handleDeleteDefence(protectionDay?.defence?.id)} />
            </div>
          </div>

          <Button
            className={css.addBtn}
            onClick={() => {
              setActiveRoom(null)
              setIsChangeRoomSettingModal(true)
              setRoomModalMode('create')
            }}
            theme='grey-outline'
          >
            <PlusIcon />
          </Button>
        </div>

        <div className={css.tableContent}>
          <div
            className={css.gridHeader}
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridTemplateColumns: `repeat(${rooms.length}, 60px minmax(400px, 1fr))`,
            }}
          >
            {rooms.map((room) => (
              <Fragment key={room.id}>
                <div className={css.numberHeader}>№</div>
                <div className={css.roomHeader}>
                  <div className={css.roomActions}>
                    <EditButton
                      onClick={() => {
                        setActiveRoom(room)
                        setIsChangeRoomSettingModal(true)
                        setRoomModalMode('update')
                      }}
                    />

                    <TrashIcon onClick={() => handleDeleteRoom(room?.id)} />
                  </div>

                  <div className={css.roomTitle}>
                    <div className={css.roomTitleName}>{truncateText(room.name, 38)}</div>
                    <p className={css.timeRooms}>{formatTime(new Date(room.startsAt))}</p>
                  </div>

                  <div className={css.roomTeachers}>{room.id}</div>

                  <a href={room.link} className={css.roomLink}>
                    {truncateText(room.link, 38)}
                  </a>
                </div>
              </Fragment>
            ))}
          </div>

          {Array.from({ length: ROW_COUNT }, (_, rowIndex) => (
            <div
              key={rowIndex}
              className={css.gridRow}
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridTemplateColumns: `repeat(${rooms.length}, 60px minmax(400px, 1fr))`,
              }}
            >
              {rooms.map((room, colIndex) => {
                const team = slots[rowIndex]?.[colIndex] ?? null

                const startTime = team
                  ? getTeamStartTime(room.startsAt, room.minutesForTeam, team.presentationOrder)
                  : null

                return (
                  <Fragment key={`${room.id}-${rowIndex}`}>
                    <div className={css.numberCell}>
                      <div>{rowIndex + 1}</div>
                      {startTime && <div className={css.teamStartTime}>{startTime}</div>}
                    </div>
                    <Cell
                      rowIndex={rowIndex}
                      roomIndex={colIndex}
                      team={team}
                      moveTeam={moveTeam}
                      onOpenModal={() => {
                        setSelectedSlot({ rowIndex, roomIndex: colIndex })
                        setIsSelectTeamModal(true)
                      }}
                      onDelete={() => {
                        if (!team) return
                        handleDeleteTeam(room.id, team.id, rowIndex, colIndex)
                      }}
                    />
                  </Fragment>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {isChangeRoomSettingModal && (
        <ChangeRoomSettingsModal
          type={roomModalMode}
          onClose={() => setIsChangeRoomSettingModal(false)}
          onRoomCreated={handleRoomCreated}
        />
      )}

      {isChangeDaySettingModal && <DefenceModal onClose={() => setIsChangeDaySettingModal(false)} type='update' />}

      {isSelectTeamModal && (
        <SelectTeamModal
          onClose={() => {
            setIsSelectTeamModal(false)
            setSelectedSlot(null)
          }}
          onSelect={selectedTeam}
        />
      )}
    </div>
  )
}
